import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "@/hooks/useTranslation";

// Drive Now Autohaus YouTube feature video
const YOUTUBE_VIDEO_ID = "MHCUcn22Edo";
const EMBED_SRC = `https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`;

/**
 * Landing-page cinematic video section.
 * Uses a poster + click-to-play facade so the page stays fast,
 * then mounts the official YouTube embed (privacy-enhanced domain).
 */
export default function FeaturedVideo() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);

  const posterUrl = `https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;

  return (
    <section
      id="why-choose"
      className={`scroll-mt-24 py-16 md:py-24 transition-colors duration-300 ${
        isDark ? "bg-[#050505]" : "bg-[#FAFAFA]"
      }`}
      data-testid="featured-video-section"
      aria-label={t("featuredVideo.sectionAria")}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-10 md:mb-12"
        >
          <span
            className={`text-sm uppercase tracking-widest font-medium mb-3 block ${
              isDark ? "text-blue-500" : "text-red-600"
            }`}
          >
            {t("featuredVideo.eyebrow")}
          </span>
          <h2
            className={`font-unbounded text-3xl md:text-4xl font-bold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {t("featuredVideo.title")}{" "}
            <span className={isDark ? "text-blue-500" : "text-red-600"}>
              {t("featuredVideo.titleAccent")}
            </span>
          </h2>
          <p
            className={`mt-3 font-manrope ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {t("featuredVideo.description")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, delay: 0.1 }}
          className="relative mx-auto max-w-5xl"
        >
          {/* Soft ambient glow behind the player */}
          <div
            className={`pointer-events-none absolute -inset-4 md:-inset-8 blur-3xl opacity-40 ${
              isDark
                ? "bg-gradient-to-br from-blue-600/30 via-transparent to-blue-900/20"
                : "bg-gradient-to-br from-red-400/25 via-transparent to-red-200/20"
            }`}
            aria-hidden
          />

          {/* Frame */}
          <div
            className={`relative overflow-hidden border shadow-2xl ${
              isDark
                ? "border-white/10 bg-black shadow-blue-900/20"
                : "border-gray-200 bg-black shadow-red-900/10"
            }`}
          >
            {/* Thin accent bar */}
            <div
              className={`h-0.5 w-full ${
                isDark
                  ? "bg-gradient-to-r from-transparent via-blue-500 to-transparent"
                  : "bg-gradient-to-r from-transparent via-red-500 to-transparent"
              }`}
            />

            <div className="relative aspect-video w-full bg-black">
              {playing ? (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`${EMBED_SRC}&autoplay=1`}
                  title="Drive Now Autohaus — YouTube video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="group absolute inset-0 w-full h-full text-left"
                  aria-label={t("featuredVideo.playAria")}
                  data-testid="featured-video-play"
                >
                  <img
                    src={posterUrl}
                    alt="Drive Now Autohaus"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback if maxres is missing
                      e.currentTarget.src = `https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`;
                    }}
                  />
                  {/* Cinematic scrim */}
                  <div
                    className={`absolute inset-0 ${
                      isDark
                        ? "bg-gradient-to-t from-black/80 via-black/25 to-black/30"
                        : "bg-gradient-to-t from-black/70 via-black/20 to-black/25"
                    }`}
                  />

                  {/* Play control */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span
                      className={`relative flex h-16 w-16 md:h-20 md:w-20 items-center justify-center border transition-all duration-300 group-hover:scale-110 ${
                        isDark
                          ? "bg-blue-600/90 border-blue-400/40 text-white shadow-[0_0_40px_rgba(37,99,235,0.45)] group-hover:bg-blue-500"
                          : "bg-red-600/95 border-red-300/50 text-white shadow-[0_0_40px_rgba(220,38,38,0.35)] group-hover:bg-red-500"
                      }`}
                    >
                      <Play className="h-7 w-7 md:h-8 md:w-8 fill-current ml-0.5" />
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                    <p
                      className={`text-xs uppercase tracking-[0.2em] mb-1 ${
                        isDark ? "text-blue-300/80" : "text-red-200"
                      }`}
                    >
                      {t("featuredVideo.watchOnYoutube")}
                    </p>
                    <p className="font-unbounded text-base md:text-lg font-semibold text-white">
                      Drive Now Autohaus
                    </p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
